/**
 * Downloads a server-generated XLSX.
 *
 * Both Takata screens build their workbook server-side with ClosedXML and hand
 * back a file response, so the browser side is the same either way: fetch with
 * the session cookie, check that what came back is actually a spreadsheet, then
 * push it through an object URL.
 *
 * The content-type check is the session guard. An expired cookie does not fail
 * the request — it answers with the login document, and without this the user
 * would get an .xlsx full of HTML instead of being sent to sign in again.
 */

const SPREADSHEET_CONTENT_TYPE = 'spreadsheetml';

export class ExportSessionExpiredError extends Error {
  constructor() {
    super('The session expired before the export could be generated.');
    this.name = 'ExportSessionExpiredError';
  }
}

/** Reads the server's filename, falling back to the caller's. */
function fileNameFrom(response: Response, fallback: string): string {
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match ? decodeURIComponent(match[1]) : fallback;
}

export async function downloadExport(url: string, fallbackFileName: string): Promise<void> {
  const response = await fetch(url, { credentials: 'same-origin' });
  const contentType = response.headers.get('Content-Type') ?? '';

  if (!response.ok || !contentType.includes(SPREADSHEET_CONTENT_TYPE)) {
    throw new ExportSessionExpiredError();
  }

  const blobUrl = window.URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileNameFrom(response, fallbackFileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}
